<template>
  <v-dialog :model-value="modelValue" max-width="840px" persistent class="gm-dialog"
    @update:model-value="handleVisibleChange">
    <v-card class="rounded-30 overflow-hidden dialog-card">
      <div class="gm-dialog-header d-flex align-center px-8 py-6">
        <div class="text-h5">
          <span style="color: #2655f1">新增</span><span>{{ customerType || '客户' }}</span>
        </div>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="close" />
      </div>
      <v-card-text class="pa-8 pt-4 pb-4 scrollbar-hide" style="max-height: 88vh; overflow-y: auto">
        <v-form>
          <div class="form-grid-2">
            <div class="form-group">
              <label>客户类型 <span class="required-star">*</span></label>
              <v-text-field :model-value="customerType" variant="outlined" density="compact" class="gm-input"
                hide-details readonly disabled />
            </div>
            <div class="form-group">
              <label>客户名称 <span class="required-star">*</span></label>
              <v-text-field v-model="form.customerName" placeholder="请输入客户名称" variant="outlined" density="compact"
                class="gm-input" hide-details />
            </div>
            <div class="form-group">
              <label>简称</label>
              <v-text-field v-model="form.shortName" placeholder="请输入简称" variant="outlined" density="compact"
                class="gm-input" hide-details />
            </div>
            <div class="form-group">
              <label>联系人</label>
              <v-text-field v-model="form.contactPerson" placeholder="请输入联系人" variant="outlined" density="compact"
                class="gm-input" hide-details />
            </div>
            <div class="form-group">
              <label>电话</label>
              <v-text-field v-model="form.phone" placeholder="请输入电话" variant="outlined" density="compact"
                class="gm-input" hide-details />
            </div>
            <div class="form-group">
              <label>邮箱</label>
              <v-text-field v-model="form.email" placeholder="请输入邮箱" variant="outlined" density="compact"
                class="gm-input" hide-details />
            </div>
            <div class="form-group grid-full">
              <label>地址</label>
              <v-text-field v-model="form.address" placeholder="请输入地址" variant="outlined" density="compact"
                class="gm-input" hide-details />
            </div>
            <div class="form-group grid-full">
              <label>备注</label>
              <v-textarea v-model="form.remark" placeholder="请输入备注" variant="outlined" density="compact"
                class="gm-input" hide-details auto-grow rows="3" />
            </div>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-8 pb-8 pt-0 justify-end">
        <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4" @click="close">
          取消
        </v-btn>
        <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="submitting"
          @click="submit">
          确定
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { eui } from 'ems-ui'
import * as customerApi from '@/api/device/customer'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  customerType: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'created'])

const submitting = ref(false)

const defaultForm = () => ({
  customerName: '',
  shortName: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  remark: '',
})

const form = reactive(defaultForm())

function resetForm() {
  Object.assign(form, defaultForm())
}

// 每次打开弹框时清空表单
watch(() => props.modelValue, (visible) => {
  if (visible) resetForm()
})

function handleVisibleChange(value) {
  emit('update:modelValue', value)
}

function close() {
  emit('update:modelValue', false)
}

function validateForm() {
  if (!props.customerType) {
    eui.showToast('缺少客户类型', 'error')
    return false
  }
  if (!form.customerName.trim()) {
    eui.showToast('请输入客户名称', 'error')
    return false
  }
  return true
}

function buildPayload() {
  return {
    customerType: props.customerType,
    customerName: form.customerName.trim(),
    shortName: form.shortName.trim(),
    contactPerson: form.contactPerson.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    address: form.address.trim(),
    remark: form.remark.trim(),
  }
}

async function submit() {
  if (!validateForm()) return
  submitting.value = true
  try {
    const payload = buildPayload()
    await customerApi.add(payload)
    eui.showToast('新增客户成功', 'success')
    emit('created', { customerType: payload.customerType, name: payload.customerName })
    close()
  } catch (error) {
    eui.showToast('新增客户失败，请稍后重试', 'error')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped lang="scss">
.dialog-card {
  border-radius: 30px !important;
  background: #fff;
}

.gm-dialog-header {
  background: linear-gradient(185deg, #2655f160 0%, #fff0 60%);
  position: relative;

  .text-h5 {
    font-size: 24px !important;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
}

.gm-btn-primary,
.gm-btn-secondary {
  height: 42px !important;
  border-radius: 12px !important;
  font-size: 16px !important;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
}

.form-group {
  min-width: 0;
}

.form-group.grid-full {
  grid-column: 1 / -1;
}

.form-group label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.72);
}

.required-star {
  color: #ff4d4f;
}
</style>
